import * as React from "react";
import { IPESWalletDetails } from "@market/interfaces/IWallet";
import { Button, Space, Typography } from "antd";
import { Link } from "react-router-dom";
import Big from "big.js";
import CurrencyAvatar from "@funds/pages/MyFunds/tabs/FundsTab/parts/CurrencyAvatar";
import { REQUEST_TO_SELL_SHARES_URL } from "@profile/pages/SharesPage/config";
import Chevron from "@images/chevron.svg";

import * as styles from "./MySharesPage.module.scss";

const big = Big();

export const colors = [
  "#1C1CF5",
  "#F29B37",
  "#1C80F5",
  "#81109C",
  "#50af95",
  "#FF6161",
];

export const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  currencyDisplay: "narrowSymbol",
});

type Props = {
  baseUrl: string;
  isMobile?: boolean;
};

export const sharesColumns = ({ baseUrl, isMobile }: Props) => [
  {
    title: "NAME",
    dataIndex: "currency_name",
    key: "currency_name",
    ellipsis: true,
    width: isMobile ? "100%" : "30%",
    sorter: (a: IPESWalletDetails, b: IPESWalletDetails) =>
      a.currency_name.localeCompare(b.currency_name),
    render: (text: string, record: IPESWalletDetails, index: number) => {
      return (
        <div className={styles.currencyName}>
          <CurrencyAvatar
            currency={record.currency}
            background={colors[index] ?? colors[colors.length - 1]}
          />
          &nbsp;
          <span>{text}</span>
        </div>
      );
    },
  },
  {
    title: "NO. OF SHARES",
    dataIndex: "balance",
    key: "balance",
    ellipsis: true,
    className: styles.textRightMobile,
    width: isMobile ? "100%" : "18%",
    sorter: (a: IPESWalletDetails, b: IPESWalletDetails) =>
      big(a.balance ?? 0).gt(b.balance ?? 0) ? 0 : 1,
    render: (_: unknown, record: IPESWalletDetails) => (
      <div className={styles.balanceCell}>
        <Typography.Text>{record.balance}</Typography.Text>
        {isMobile && <Chevron />}
      </div>
    ),
  },
  {
    title: "Share Price",
    dataIndex: "share_price",
    key: "share_price",
    className: styles.hideOnMobile,
    ellipsis: true,
    width: isMobile ? 0 : "17%",
    sorter: (a: IPESWalletDetails, b: IPESWalletDetails) => {
      const valueA =
        typeof a.share_price === "string"
          ? big(Number.MAX_SAFE_INTEGER)
          : big(a.share_price ?? 0);
      const valueB =
        typeof b.share_price === "string"
          ? big(Number.MAX_SAFE_INTEGER)
          : big(b.share_price ?? 0);
      return valueA.gt(valueB) ? 1 : -1;
    },
    render: (_: unknown, record: IPESWalletDetails) => (
      <Typography.Text>
        {typeof record.share_price === "string"
          ? record.share_price
          : formatter.format(record.share_price ?? 0)}
      </Typography.Text>
    ),
  },
  {
    title: "VALUATION",
    dataIndex: "total_usd",
    key: "total_usd",
    className: styles.hideOnMobile,
    ellipsis: true,
    width: isMobile ? 0 : "15%",
    sorter: (a: IPESWalletDetails, b: IPESWalletDetails) => {
      const parseValue = (value: string | number | null): Big => {
        if (typeof value === "string") {
          const parsedNumber = parseFloat(value);
          return isNaN(parsedNumber)
            ? big(Number.MAX_SAFE_INTEGER)
            : big(parsedNumber);
        } else if (typeof value === "number") {
          return big(value);
        } else {
          return big(Number.MAX_SAFE_INTEGER);
        }
      };

      const valueA = parseValue(a.total_usd);
      const valueB = parseValue(b.total_usd);

      return valueA.gt(valueB) ? 1 : -1;
    },
    render: (_: unknown, record: IPESWalletDetails) => (
      <Typography.Text>
        {typeof record.total_usd === "string" &&
        isNaN(parseFloat(record.total_usd))
          ? record.total_usd
          : formatter.format(parseFloat(record.total_usd.toString() ?? "0"))}
      </Typography.Text>
    ),
  },
  {
    title: "ACTION",
    dataIndex: "country",
    key: "country",
    className: styles.hideOnMobile,
    align: "right" as const,
    width: isMobile ? 0 : "20%",
    render: (_: string, row: IPESWalletDetails) => {
      return (
        <Space wrap className={styles.actions}>
          <Link
            to={`${baseUrl}${REQUEST_TO_SELL_SHARES_URL}?share=${row.currency}`}
          >
            <Button>Request to sell</Button>
          </Link>
        </Space>
      );
    },
  },
];
